<script lang="ts" setup>
import type { Ref } from 'vue'

import BasicInput from '~/components/form-elements/BasicInput.vue'
import OrganizationSelect from '~/components/form-elements/select/OrganizationSelect.vue'
import { Mask, MaskUtils } from '~/enums/mask'
import OrganizationType from '~/enums/organization-type'
import helpers from '~/helpers'
import { useDadataStore } from '~/store/dadata'
import type { OrganizationSuggestionData } from '~/types/dadata'
import type { SelectedItem } from '~/types/form-elements'

const emptyForm = {
  address: null, // "Почтовый адрес".
  bankAccount: null, // "Расчётный счёт".
  bankName: null, // "Наименование банка".
  correspondentAccount: null, // "Корреспондентский счёт".
  name: null, // "Наименование ИП".
  psrnie: null, // "ОГРНИП".
  rcbic: null, // "БИК".
  tin: null // "ИНН".
}
const maskaOptions = {
  account: MaskUtils.getMaskaOptions(Mask.Account),
  psrnie: MaskUtils.getMaskaOptions(Mask.Psrnie),
  rcbic: MaskUtils.getMaskaOptions(Mask.Rcbic),
  tin: MaskUtils.getMaskaOptions(Mask.Tin)
}

const dadataStore = useDadataStore()
const { getOrganizationSuggestions } = storeToRefs(dadataStore)

const form = ref<any>({ ...emptyForm })

const address = ref(null)
const bankAccount = ref(null)
const bankName = ref(null)
const correspondentAccount = ref(null)
const name = ref(null)
const psrnie = ref(null)
const rcbic = ref(null)

function getData (): any {
  return {
    legalSubjectData: { ...form.value }
  }
}

function resetData (): void {
  form.value = { ...emptyForm }
}

function validateAll (): string[] {
  return [
    name,
    address,
    psrnie,
    bankName,
    bankAccount,
    correspondentAccount,
    rcbic
  ]
    .map((x: Ref): string | null => helpers.validateField(x.value))
    .filter((x: string | null): boolean => x !== null) as string[]
}

function setOrganization (x: SelectedItem): void {
  const organization = getOrganizationSuggestions.value.find((org: OrganizationSuggestionData) => org.id === x.id)

  if (!organization) {
    return
  }

  form.value.tin = organization.attributes.tin
  form.value.name = organization.meta.value
  form.value.legalAddress = organization.attributes.legalAddress
  form.value.kpp = organization.attributes.kpp
  form.value.psrnie = organization.attributes.psrn
}

onUnmounted((): void => {
  dadataStore.dispose()
})

defineExpose({ getData, resetData, validateAll })
</script>

<template>
  <div>
    <div class="row mb-6">
      <div class="column-6 tb12 with-margin-bottom-24">
        <OrganizationSelect
          class="mb-6"
          :organization-type="OrganizationType.Individual"
          :tin-length="12"
          @set-organization="setOrganization"
        />
      </div>
      <div class="column-6 tb12">
        <BasicInput
          ref="name"
          v-model="form.name"
          is-required
          label-text="Наименование ИП"
        />
      </div>
    </div>

    <div class="row mb-6">
      <div class="column-6 tb12 with-margin-bottom-24">
        <BasicInput
          ref="address"
          v-model="form.address"
          is-required
          label-text="Почтовый адрес"
        />
      </div>
      <div class="column-6 tb12">
        <BasicInput
          ref="psrnie"
          v-model="form.psrnie"
          v-maska="maskaOptions.psrnie"
          is-required
          label-text="ОГРНИП"
          :max-length="15"
          :validate="(x: string): boolean => x.length === 15"
        />
      </div>
    </div>

    <div class="row mb-6">
      <div class="column-6 tb12 with-margin-bottom-24">
        <BasicInput
          ref="bankName"
          v-model="form.bankName"
          is-required
          label-text="Наименование банка"
        />
      </div>
      <div class="column-6 tb12">
        <BasicInput
          ref="rcbic"
          v-model="form.rcbic"
          v-maska="maskaOptions.rcbic"
          is-required
          label-text="БИК"
          :max-length="9"
          :validate="(x: string): boolean => x.length === 9"
        />
      </div>
    </div>

    <div class="row">
      <div class="column-6 tb12 with-margin-bottom-24">
        <BasicInput
          ref="bankAccount"
          v-model="form.bankAccount"
          v-maska="maskaOptions.account"
          is-required
          label-text="Расчётный счёт"
          :max-length="20"
          :validate="(x: string): boolean => x.length === 20"
        />
      </div>
      <div class="column-6 tb12">
        <BasicInput
          ref="correspondentAccount"
          v-model="form.correspondentAccount"
          v-maska="maskaOptions.account"
          is-required
          label-text="Корреспондентский счёт"
          :max-length="20"
          :validate="(x: string): boolean => x.length === 20"
        />
      </div>
    </div>
  </div>
</template>
